import {Component, HostListener, OnInit} from '@angular/core';
import { Kpi } from '../../model/kpi';
import { Tenant } from '../../model/tenant';
import { KpiFilters } from '../../model/filters/kpi-filters';
import { KpisService } from '../../services/kpis.service';
import { TenantsService } from '../../services/tenants.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { AuthGuard } from '../../common/auth-guard.service';
import { CommonMessagesService } from '../../services/common-messages.service';
import { SortOrder } from '../../model/sort-order';
import { Organization } from '../../model/organization';
import { OrganizationsService } from '../../services/organizations.service';
import {FiltersService} from "../../services/filters.service";

@Component({
  selector: 'app-kpis',
  templateUrl: './kpis.component.html',
  styleUrls: ['./kpis.component.scss']
})
export class KpisComponent implements OnInit {

  public kpis: Kpi[];
  public totalKpis: number;
  public loadingKpis: boolean;
  public tenants: Tenant[];
  public organizations: Organization[];

  public rows = 10;
  public first = 0;
  public selected = [];

  public filtersVisible: boolean;
  public currentFilters: KpiFilters;
  public filtersFilled: boolean;
  public exportVisible: boolean;

  constructor(private kpisService: KpisService,
              private messageService: MessageService,
              private organizationsService: OrganizationsService,
              private i18n: TranslocoService,
              private tenantsService: TenantsService,
              public authGuardService: AuthGuard,
              private confirmationService: ConfirmationService,
              private commonMessagesService: CommonMessagesService,
              private filtersService: FiltersService<KpiFilters>) {
    this.currentFilters = new KpiFilters();
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    this.kpis = new Array(this.rows);
    this.loadFilters().then(()=> {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
    });
  }

  private loadKpis(offset?: number, limit?: number): void {
    this.loadingKpis = true;
    this.kpis = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.kpisService.getKpis(offset, limit, this.filtersService.getCurrentFilters(this.constructor.name) === undefined ? this.currentFilters: this.filtersService.getCurrentFilters(this.constructor.name))
      .subscribe(response => {
        this.kpis = response.kpis;
        this.totalKpis = response.totalElements;
        this.loadingKpis = false;
      });
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadKpis(event.first / event.rows, event.rows);
  }

  resetFilters(): void {
    this.currentFilters = new KpiFilters();
    this.currentFilters.sortField = 'name';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true){
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadKpis(this.first / this.rows, this.rows);
  }

  confirmAndDelete(kpi: Kpi): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteKpi(kpi.uuid);
    });
  }

  deleteKpi(uuid: string): void {
    this.kpisService.deleteKpi(uuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadKpis(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if (this.selected.length === 0) { return; }
    const uuids = this.selected.map(item => item.uuid);

    this.kpisService.deleteKpis(uuids).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadKpis(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  trimFieldsForm(filters: KpiFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  get filteredOrganizations(): Organization[] {
    if (this.authGuardService.superAdminRequired()) {
      if (this.currentFilters.tenantUuids?.length > 0) {
        return this.organizations.filter(org => this.currentFilters.tenantUuids.includes(org.tenant.uuid));
      }
    }
    return this.organizations;
  }

  private loadFilters(): Promise<any> {
    let promises: Array<Promise<any>> = [this.organizationsService.getOrganizations().toPromise()];
    if (this.authGuardService.superAdminRequired()) {
      promises.push(this.tenantsService.getTenants().toPromise());
    }
    return Promise.all(promises).then(response => {
      this.organizations = response[0].organizations.sort((a, b) => a.name > b.name ? 1 : -1);

      if (this.authGuardService.superAdminRequired()) {
        this.tenants = response[1].tenants.sort((a, b) => a.name > b.name ? 1 : -1);
      }
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
